import axios from 'axios';
import getUrl from './getUrl';

/**
 * Get secure URL for a protected file
 * This function fetches a temporary access token from the backend
 * and returns a secure URL with the token
 *
 * @param {Object|string} file - File object from Strapi or file ID
 * @returns {Promise<string>} - Secure file URL with token
 */
export default async function getSecureFileUrl(file) {
  if (!file) {
    throw new Error('File object or ID is required');
  }

  // Extract file ID
  let fileId;
  if (typeof file === 'string') {
    fileId = file;
  } else if (file.id) {
    fileId = file.id;
  } else if (file.Document && file.Document.id) {
    fileId = file.Document.id;
  } else {
    throw new Error('Could not extract file ID from provided object');
  }

  try {
    // Request secure URL from backend
    const response = await axios.post(
      getUrl('/secure-file/get-url'),
      { fileId },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (response.data && response.data.url) {
      return response.data.url;
    }

    throw new Error('Invalid response from secure file API');
  } catch (error) {
    console.error('Error getting secure file URL:', error);

    // Fallback to direct URL for development/testing
    // WARNING: This should be removed in production
    if (process.env.NODE_ENV === 'development') {
      console.warn('Falling back to direct URL (development only)');
      if (typeof file === 'object' && file.url) {
        return getUrl(file.url);
      }
      if (typeof file === 'object' && file.Document && file.Document.url) {
        return getUrl(file.Document.url);
      }
    }

    throw error;
  }
}

/**
 * Get secure URL for a file with caching
 * Caches the token for the lifetime of the token (typically 1 hour)
 *
 * @param {Object|string} file - File object from Strapi or file ID
 * @returns {Promise<string>} - Secure file URL with token
 */
const urlCache = new Map();

export async function getSecureFileUrlCached(file) {
  const fileId = typeof file === 'string' ? file : (file.id || file.Document?.id);

  if (!fileId) {
    throw new Error('Could not extract file ID');
  }

  // Check cache
  const cached = urlCache.get(fileId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  // Get new URL
  const url = await getSecureFileUrl(file);

  // Cache for 55 minutes (5 minutes before actual expiry)
  urlCache.set(fileId, {
    url,
    expiresAt: Date.now() + (55 * 60 * 1000),
  });

  return url;
}

/**
 * Clear cached URLs for a file
 * @param {string} fileId - File ID to clear from cache
 */
export function clearSecureFileUrlCache(fileId) {
  if (fileId) {
    urlCache.delete(fileId);
  } else {
    urlCache.clear();
  }
}
